import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useLibraryFunctions } from '../hooks/useLibraryFunctions'
import { C, F, DEPTS, DEPT_KEYS } from '../components/forecast/theme'
import { Card, Head, Btn, DeptChip, Icon, Pill } from '../components/forecast/prims'
import type { LibraryFunction, LibraryFunctionBody } from '../types'

// EM-8 · Core NYMR Functions library. The reusable catalogue of delivery
// functions (role · dept · default commitments · lead time) pulled into any
// forecast team. Admin surface (route-gated by AdminRoute). Mirrors the
// existing library admin surfaces: manage once, snapshot on use; slug is the
// immutable public contract; usedInCount reflects live usage.

export function AdminLibraryFunctions() {
  const { functions, isLoading, create, patch, remove } = useLibraryFunctions()
  const [editing, setEditing] = useState<LibraryFunction | 'new' | null>(null)

  return (
    <div style={{ fontFamily: F.body }}>
      <Head
        eyebrow="Forecast Studio · library"
        title="Core NYMR Functions"
        sub="Reusable delivery functions pulled into forecast teams. Manage them once here; each forecast snapshots the function set it uses."
        right={<Btn variant="brass" icon="plus" onClick={() => setEditing('new')}>New function</Btn>}
      />

      {isLoading && <div style={{ fontFamily: F.mono, fontSize: 13, color: C.inkSoft }}>Loading functions…</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 16 }}>
        {functions.map((f) => (
          <Card key={f.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <DeptChip id={f.dept} />
              {f.kind === 'exec-signoff' && <Pill fg={C.brassDk} bg={C.brassSoft} icon="star">Exec sign-off</Pill>}
              <Pill fg={C.inkSoft} bg={C.paper}>{f.usedInCount ?? 0} in use</Pill>
              <span style={{ marginLeft: 'auto' }}>
                <Btn variant="ghost" small icon="edit" onClick={() => setEditing(f)}>Edit</Btn>
              </span>
            </div>
            <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: C.ink, lineHeight: 1.15 }}>{f.title}</div>
            <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: C.inkFaint, margin: '12px 0 6px' }}>Default commitments</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {f.defaultResponsibilities.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                  <span style={{ marginTop: 2 }}><Icon name="check" size={12} color={C.green} /></span>
                  <span style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft }}>{r}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.lineSoft}`, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="clock" size={13} color={C.inkFaint} />
              <span style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft }}>Lead time <b style={{ color: C.ink }}>{f.defaultLeadWeeks}w</b> before event</span>
              <span style={{ marginLeft: 'auto', fontFamily: F.mono, fontSize: 10.5, color: C.inkFaint }}>{f.slug}</span>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <FunctionEditor
          fn={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onCreate={async (b) => { await create(b); setEditing(null) }}
          onPatch={async (id, b) => { await patch(id, b); setEditing(null) }}
          onDelete={async (id) => { await remove(id); setEditing(null) }}
        />
      )}
    </div>
  )
}

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
}

function FunctionEditor({ fn, onClose, onCreate, onPatch, onDelete }: {
  fn: LibraryFunction | null
  onClose: () => void
  onCreate: (b: LibraryFunctionBody) => Promise<void>
  onPatch: (id: number, b: Partial<LibraryFunctionBody>) => Promise<void>
  onDelete: (id: number) => Promise<void>
}) {
  const isNew = fn === null
  const [title, setTitle] = useState(fn?.title ?? '')
  const [slug, setSlug] = useState(fn?.slug ?? '')
  const [dept, setDept] = useState(fn?.dept ?? 'events')
  const [kind, setKind] = useState<'standard' | 'exec-signoff'>(fn?.kind ?? 'standard')
  const [leadWeeks, setLeadWeeks] = useState(String(fn?.defaultLeadWeeks ?? 8))
  const [resp, setResp] = useState((fn?.defaultResponsibilities ?? []).join('\n'))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const effectiveSlug = isNew ? (slug || slugify(title)) : fn!.slug
  const valid = title.trim().length > 0 && (!isNew || effectiveSlug.length > 0)

  async function save() {
    if (!valid || busy) return
    setBusy(true); setErr(null)
    const responsibilities = resp.split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 12)
    const lead = Math.max(0, Math.min(104, Number(leadWeeks) || 8))
    try {
      if (isNew) await onCreate({ slug: effectiveSlug, title: title.trim(), dept, kind, defaultResponsibilities: responsibilities, defaultLeadWeeks: lead })
      else await onPatch(fn!.id, { title: title.trim(), dept, kind, defaultResponsibilities: responsibilities, defaultLeadWeeks: lead })
    } catch (e) { setErr((e as Error).message); setBusy(false) }
  }

  const label: React.CSSProperties = { fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: C.inkSoft, marginBottom: 5, display: 'block' }
  const input: React.CSSProperties = { width: '100%', border: `1px solid ${C.line}`, borderRadius: 9, padding: '9px 11px', fontFamily: F.body, fontSize: 13.5, color: C.ink, background: C.card, outline: 'none' }

  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,50,42,0.45)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: '94%', maxHeight: '90vh', overflowY: 'auto' }}>
        <Card pad={22}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 800, color: C.ink }}>{isNew ? 'New function' : 'Edit function'}</div>
            <span style={{ marginLeft: 'auto' }}><Btn variant="ghost" small icon="x" onClick={onClose}>Close</Btn></span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={label}>Title</label>
              <input style={input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Event Lead" autoFocus />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={label}>Department</label>
                <select style={input} value={dept} onChange={(e) => setDept(e.target.value)}>
                  {DEPT_KEYS.map((k) => <option key={k} value={k}>{DEPTS[k]!.name}</option>)}
                </select>
              </div>
              <div>
                <label style={label}>Lead time (weeks)</label>
                <input style={input} type="number" min={0} max={104} value={leadWeeks} onChange={(e) => setLeadWeeks(e.target.value)} />
              </div>
            </div>
            <div>
              <label style={label}>Function type</label>
              <select style={input} value={kind} onChange={(e) => setKind(e.target.value as 'standard' | 'exec-signoff')}>
                <option value="standard">Standard delivery function</option>
                <option value="exec-signoff">Executive sign-off (reviews the business case; ties into the change notice)</option>
              </select>
            </div>
            <div>
              <label style={label}>Slug {isNew ? '' : '(fixed)'}</label>
              <input style={{ ...input, fontFamily: F.mono, opacity: isNew ? 1 : 0.6 }} value={effectiveSlug} onChange={(e) => setSlug(slugify(e.target.value))} disabled={!isNew} placeholder="auto from title" />
            </div>
            <div>
              <label style={label}>Default commitments (one per line)</label>
              <textarea style={{ ...input, resize: 'vertical', minHeight: 90, fontFamily: F.body }} value={resp} onChange={(e) => setResp(e.target.value)} placeholder={'Owns the concept & business case\nChairs the review board'} />
            </div>
            {err && <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.oxblood, background: C.oxbloodSoft, borderRadius: 8, padding: '8px 11px' }}>{err}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
              {!isNew && <Btn variant="danger" small icon="x" onClick={() => onDelete(fn!.id)}>Remove</Btn>}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
                <Btn variant="primary" icon="check" onClick={save} disabled={!valid || busy}>{busy ? 'Saving…' : (isNew ? 'Create function' : 'Save changes')}</Btn>
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>,
    document.body
  )
}
